'use client';

import { cn, getQueryParams } from '@wk/wk-uni-lib';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useState, useRef, useMemo, useLayoutEffect, useEffect } from 'react';

import { useLanguage } from '@/hooks/use-language';
import { useTraceCommonParams } from '@/hooks/use-trace-common-params';

const navTraceEventMap = {
  0: { click: 'C_TopNav_Home', exposure: 'D_Home' },
  1: { click: 'C_TopNav_Study', exposure: 'D_Study' },
  2: { click: 'C_TopNav_Resource', exposure: 'D_Resource' },
  3: { click: 'C_TopNav_Account', exposure: 'D_Account' },
  4: { click: 'C_TopNav_Rewards', exposure: 'D_Rewards' },
};
interface NavigationLinksProps {
  dictionary: Record<string, string>;
  isErrorPage?: boolean;
}

export default function NavigationLinks({
  dictionary,
  isErrorPage = false,
}: NavigationLinksProps) {
  const [activeIndex, setActiveIndex] = useState(-1);
  const [indicatorStyle, setIndicatorStyle] = useState({
    width: 0,
    left: 0,
  });
  const [hasUserInteraction, setHasUserInteraction] = useState(false);
  const pathname = usePathname();
  const { currentLanguage } = useLanguage();
  const { traceWithCommonParams } = useTraceCommonParams();
  const linkRefs = useRef<(HTMLAnchorElement | null)[]>([]);

  const navItems = useMemo(
    () => [
      { name: dictionary['首页'], href: '/home' },
      { name: dictionary['学习'], href: '/study' },
      { name: dictionary['资源'], href: '/resource' },
      { name: dictionary['积分'], href: '/rewards' },
      { name: dictionary['账户'], href: '/account' },
    ],
    [dictionary],
  );

  // 更新激活的导航项索引
  useLayoutEffect(() => {
    const currentIndex = navItems.findIndex(
      ({ href }) =>
        pathname.startsWith(href) || pathname.startsWith(`/zh${href}`),
    );
    setActiveIndex(currentIndex >= 0 ? currentIndex : -1);
  }, [pathname, navItems]);

  // 初始化时设置指示器位置（无动画）
  useLayoutEffect(() => {
    if (activeIndex >= 0) {
      const activeLink = linkRefs.current[activeIndex];
      if (activeLink) {
        const rect = activeLink.getBoundingClientRect();
        const parentRect = activeLink.parentElement?.getBoundingClientRect();

        if (parentRect) {
          setIndicatorStyle({
            width: rect.width,
            left: rect.left - parentRect.left,
          });
        }
      }
    }
  }, [activeIndex]);

  useEffect(() => {
    if (activeIndex >= 0) {
      const searchParams = getQueryParams();

      let dsSource = searchParams?.dsSource;

      if (!dsSource && hasUserInteraction) {
        dsSource = 'web_顶部导航';
      }

      if (!dsSource && !hasUserInteraction) {
        dsSource = '其他';
      }

      if (!isErrorPage) {
        traceWithCommonParams({
          _event:
            navTraceEventMap[activeIndex as keyof typeof navTraceEventMap]
              .exposure,
          dsSource: dsSource as string,
        });
      }
    }
  }, [activeIndex, hasUserInteraction]);

  // 处理导航项点击
  const handleNavClick = (index: number) => {
    if (!isErrorPage) {
      traceWithCommonParams({
        _event: navTraceEventMap[index as keyof typeof navTraceEventMap].click,
      });
    }
    if (!hasUserInteraction) {
      setHasUserInteraction(true);
    }

    // 更新指示器样式（带动画）
    const activeLink = linkRefs.current[index];
    if (activeLink) {
      const rect = activeLink.getBoundingClientRect();
      const parentRect = activeLink.parentElement?.getBoundingClientRect();

      if (parentRect) {
        setIndicatorStyle({
          width: rect.width,
          left: rect.left - parentRect.left,
        });
      }
    }

    setActiveIndex(index);
  };

  return (
    <div className="relative ml-16 h-full">
      {navItems.map((item, index) => (
        <Link
          key={item.href}
          ref={el => {
            linkRefs.current[index] = el;
          }}
          href={`/${currentLanguage}${item.href}`}
          onClick={() => handleNavClick(index)}
          className={cn(
            'mr-8 inline-block h-full leading-[68px]',
            activeIndex === index && 'font-bold',
            index === navItems.length - 1 && 'mr-0',
          )}>
          {item.name}
        </Link>
      ))}
      {activeIndex >= 0 && (
        <div
          className={cn(
            'absolute bottom-0 h-1 rounded-se-[40px] rounded-ss-[40px] bg-brand-red-100',
            hasUserInteraction && 'transition-all duration-300 ease-out',
          )}
          style={{
            width: `${indicatorStyle.width}px`,
            left: `${indicatorStyle.left}px`,
          }}
        />
      )}
    </div>
  );
}
